import { appApi } from "@/api/modules/app";
import { wgs84ToGcj02 } from "./coordinate";
import { storage, storageKeys } from "./storage";

export interface UserLocation {
  longitude: number;
  latitude: number;
  accuracy?: number;
  timestamp: number;
  coordinateSystem?: "gcj02";
}

const LOCATION_CHANGE_EVENT = "athome:location-change";
const DEFAULT_LOCATION_MAX_AGE = 24 * 60 * 60 * 1000;
const WECHAT_JS_SDK_URL = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";
const WECHAT_LOCATION_API_LIST = ["getLocation"];
const WECHAT_LOCATION_READY_TIMEOUT = 8000;
const WECHAT_LOCATION_REQUEST_TIMEOUT = 10000;

let wechatSdkLoading: Promise<void> | null = null;
let wechatLocationConfiguredUrl = "";
let wechatLocationConfigReady: Promise<void> | null = null;

function emitLocationChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(LOCATION_CHANGE_EVENT));
  }
}

export const locationStore = {
  get() {
    return storage.get<UserLocation>(storageKeys.location);
  },

  getFresh(maxAgeMs = DEFAULT_LOCATION_MAX_AGE) {
    const value = storage.get<UserLocation>(storageKeys.location);
    if (!value) {
      return null;
    }
    if (value.coordinateSystem !== "gcj02" || Date.now() - value.timestamp > maxAgeMs) {
      storage.remove(storageKeys.location);
      emitLocationChange();
      return null;
    }
    return value;
  },

  set(value: UserLocation) {
    storage.set(storageKeys.location, value);
    emitLocationChange();
  },

  clear() {
    storage.remove(storageKeys.location);
    emitLocationChange();
  },

  subscribe(listener: () => void) {
    if (typeof window === "undefined") {
      return () => {};
    }
    window.addEventListener(LOCATION_CHANGE_EVENT, listener);
    return () => window.removeEventListener(LOCATION_CHANGE_EVENT, listener);
  },
};

function isWechatBrowser() {
  return typeof navigator !== "undefined" && /MicroMessenger/i.test(navigator.userAgent);
}

function isWechatIos() {
  return isWechatBrowser() && /iP(hone|ad|od)/i.test(navigator.userAgent);
}

function normalizeSignUrl(href: string) {
  const url = new URL(href);
  url.hash = "";
  return url.toString();
}

const wechatEntrySignUrl = typeof window === "undefined" ? "" : normalizeSignUrl(window.location.href);

function currentWechatSignUrl() {
  return isWechatIos() ? wechatEntrySignUrl : normalizeSignUrl(window.location.href);
}

function describeWechatError(fallback: string, error?: unknown) {
  if (error && typeof error === "object" && "errMsg" in error) {
    return `${fallback}: ${String((error as { errMsg?: unknown }).errMsg || "")}`;
  }
  return fallback;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timer: ReturnType<typeof window.setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = window.setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer) {
      window.clearTimeout(timer);
    }
  });
}

function loadWechatSdk() {
  if (window.wx) {
    return Promise.resolve();
  }
  if (wechatSdkLoading) {
    return wechatSdkLoading;
  }
  wechatSdkLoading = new Promise<void>((resolve, reject) => {
    const existed = document.querySelector<HTMLScriptElement>(`script[src="${WECHAT_JS_SDK_URL}"]`);
    if (existed) {
      if (window.wx) {
        resolve();
        return;
      }
      existed.addEventListener("load", () => resolve(), { once: true });
      existed.addEventListener("error", () => reject(new Error("微信定位组件加载失败")), { once: true });
      window.setTimeout(() => {
        if (window.wx) {
          resolve();
        }
      }, 0);
      return;
    }
    const script = document.createElement("script");
    script.src = WECHAT_JS_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("微信定位组件加载失败"));
    document.head.appendChild(script);
  });
  return wechatSdkLoading;
}

async function ensureWechatLocationReady() {
  if (!isWechatBrowser()) {
    return false;
  }
  await loadWechatSdk();
  if (!window.wx) {
    throw new Error("微信定位组件不可用");
  }
  const signUrl = currentWechatSignUrl();
  if (wechatLocationConfigReady && wechatLocationConfiguredUrl === signUrl) {
    await wechatLocationConfigReady;
    return true;
  }

  wechatLocationConfiguredUrl = signUrl;
  wechatLocationConfigReady = withTimeout(
    appApi.getWechatJsConfig({ url: signUrl }).then((config) => new Promise<void>((resolve, reject) => {
      if (!config?.appId || !config.signature) {
        reject(new Error("微信定位签名未配置"));
        return;
      }
      window.wx?.ready(() => resolve());
      window.wx?.error((error) => reject(error instanceof Error ? error : new Error(describeWechatError("微信定位初始化失败", error))));
      window.wx?.config({
        debug: false,
        appId: config.appId,
        timestamp: config.timestamp,
        nonceStr: config.nonceStr,
        signature: config.signature,
        jsApiList: WECHAT_LOCATION_API_LIST,
      });
    })),
    WECHAT_LOCATION_READY_TIMEOUT,
    "微信定位初始化超时"
  );
  try {
    await wechatLocationConfigReady;
  } catch (error) {
    wechatLocationConfigReady = null;
    throw error;
  }
  return true;
}

async function requestWechatLocation(): Promise<UserLocation | null> {
  const ready = await ensureWechatLocationReady();
  if (!ready) {
    return null;
  }
  return withTimeout(
    new Promise<UserLocation>((resolve, reject) => {
      if (!window.wx?.getLocation) {
        reject(new Error("微信定位接口不可用"));
        return;
      }
      window.wx.getLocation({
        type: "gcj02",
        success: (result) => {
          const latitude = Number(result.latitude);
          const longitude = Number(result.longitude);
          if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            reject(new Error("微信定位没有返回有效坐标"));
            return;
          }
          resolve({
            latitude,
            longitude,
            accuracy: Number(result.accuracy) || undefined,
            timestamp: Date.now(),
            coordinateSystem: "gcj02",
          });
        },
        cancel: () => reject(new Error("已取消微信定位授权")),
        fail: (error) => reject(new Error(describeWechatError("微信定位失败", error))),
      });
    }),
    WECHAT_LOCATION_REQUEST_TIMEOUT,
    "微信定位超时"
  );
}

async function requestBrowserLocation(): Promise<UserLocation> {
  if (typeof window === "undefined" || !("geolocation" in navigator)) {
    throw new Error("当前环境不支持定位");
  }

  const position = await new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });
  });

  const gcj02 = wgs84ToGcj02(position.coords.latitude, position.coords.longitude);
  const location: UserLocation = {
    longitude: gcj02.longitude,
    latitude: gcj02.latitude,
    accuracy: position.coords.accuracy,
    timestamp: Date.now(),
    coordinateSystem: "gcj02",
  };

  return location;
}

export async function requestCurrentLocation(): Promise<UserLocation> {
  const wechatLocation = await requestWechatLocation();
  const location = wechatLocation ?? await requestBrowserLocation();

  locationStore.set(location);
  return location;
}
